import React from 'react';
import { CheckCircle, Clock, AlertTriangle, Calendar, ArrowRight } from 'lucide-react';

const WorkerProjectCard = ({
  project,
  lifecycleStatus,
  currentUserRole,
  onNavigateToPhase
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';

    try {
      return new Date(dateString)?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const normalizeStatus = (status) => {
    return String(status || 'pending')?.toLowerCase();
  };

  const isPhaseActive = (phase) => {
    const status = normalizeStatus(phase?.status);

    return (
      status === 'ongoing' ||
      status === 'in-progress' ||
      status === 'active'
    );
  };

  const isPhaseCompleted = (phase) => {
    return normalizeStatus(phase?.status) === 'completed';
  };

  const handleManagePhase = (phase) => {
    if (!phase?.phaseId) {
      console.error('Cannot navigate: missing phaseId', phase);
      return;
    }

    if (!project?.id) {
      console.error('Cannot navigate: missing project id', project);
      return;
    }

    onNavigateToPhase?.(project?.id, project?.name, phase);
  };

  const lifecycleBadge = {
    pending: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      icon: <Clock className="w-3 h-3" />
    },
    dormant: {
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      icon: <Clock className="w-3 h-3" />
    },
    active: {
      label: 'Active',
      className: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: <Clock className="w-3 h-3" />
    },
    completed: {
      label: 'Completed',
      className: 'bg-green-100 text-green-800 border-green-200',
      icon: <CheckCircle className="w-3 h-3" />
    }
  };

  const badge = lifecycleBadge?.[lifecycleStatus] || lifecycleBadge?.pending;

  const getPhaseStatusBadge = (phase) => {
    if (isPhaseCompleted(phase)) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          <CheckCircle className="w-3 h-3" />
          Completed
        </span>
      );
    }

    if (isPhaseActive(phase)) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          <Clock className="w-3 h-3" />
          Ongoing
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
        <Calendar className="w-3 h-3" />
        Pending
      </span>
    );
  };

  const workerPhases = project?.workerPhases || [];
  const alerts = project?.alerts || [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-gray-900 leading-tight">
            {project?.name}
          </h3>

          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${badge?.className}`}>
            {badge?.icon}
            {badge?.label}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(project?.startDate)}</span>
          <span className="text-gray-300">→</span>
          <span>{formatDate(project?.endDate)}</span>
        </div>
      </div>

      {alerts?.length > 0 && (
        <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />

            <div>
              <p className="text-xs font-semibold text-amber-900 mb-1">Project Alerts</p>

              {alerts?.slice(0, 2)?.map((alert, idx) => (
                <p key={idx} className="text-xs text-amber-800">
                  {alert}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="p-5">
        <p className="text-sm font-semibold text-gray-700 mb-3">Your Assigned Phase(s)</p>

        {workerPhases?.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No phases assigned yet</p>
        ) : (
          <div className="space-y-3">
            {workerPhases?.map((phase, index) => {
              const progress = phase?.progress || 0;
              const isActive = isPhaseActive(phase);
              const isCompleted = isPhaseCompleted(phase);

              return (
                <div
                  key={phase?.phaseId || index}
                  className={`rounded-lg border-2 p-4 ${
                    isActive
                      ? 'border-blue-400 bg-blue-50'
                      : isCompleted
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900 capitalize text-sm">
                      {phase?.phaseName}
                    </span>

                    {getPhaseStatusBadge(phase)}
                  </div>

                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Start: {formatDate(phase?.estimatedStartDate)}
                    </span>

                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      End: {formatDate(phase?.estimatedEndDate)}
                    </span>
                  </div>

                  {isActive && (
                    <div className="mb-3">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span className="font-medium">{progress}%</span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-blue-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {isActive && (
                    <button
                      type="button"
                      onClick={() => handleManagePhase(phase)}
                      className="mt-1 w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      Manage Phase
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkerProjectCard;
